import React from 'react';
import Time from '../../Components/Formatting/Time';
import { classNames, timeToPx } from '../../Services/helpers';
import { useGrants } from '../../Data/Business';
import Grant from '../../Components/Formatting/Grant';
import Duration from '../../Components/Formatting/Duration';
import Loading from '../../Components/Loading';
import Money from '../../Components/Formatting/Money';
import { Droppable } from "@hello-pangea/dnd";
import Appointment from './Appointment';
import Job from './Job';
import { useCalendarContext } from './Provider';
import State from '../../Data/State';

const DayAppointments = ({day, appointments, scheduledWithoutAppointments}) => {
    const {activeGrants}  = useGrants();
    const {shownGrants} = useCalendarContext();

    return <div className="grow shrink-0 h-full w-full">

        <div className="flex flex-row items-stretch h-full divide-x divide-dashed">
            {activeGrants.map(grant => {
                if (!shownGrants?.[grant.id]) {
                    return null;
                }

                return <GrantColumn key={grant.id} {...{grant, ...{day, appointments, scheduledWithoutAppointments}}} />
            })}

            {shownGrants?.unassigned && <GrantColumn key="unassigned" {...{grant: null, ...{day, appointments, scheduledWithoutAppointments}}} />}
        </div>

    </div>;
}

const GrantColumn = ({grant, day, appointments, scheduledWithoutAppointments}) => {
    const assignedApps = appointments?.filter(app => app.assignedTo === (grant?.userId ?? null)) ?? [];
    const assignedJobs = scheduledWithoutAppointments?.filter(job => job.assignedTo === (grant?.userId ?? null)) ?? [];
    const {jobsMovedTo, suggestedJobs} = useCalendarContext();

    const incomingJobIds = jobsMovedTo(grant?.userId ?? 'null', day.date);
    const noJobs = assignedApps?.length === 0 && assignedJobs?.length === 0 && incomingJobIds?.length === 0 && suggestedJobs[day.date]?.length === 0;

    return <Droppable droppableId={`${grant?.userId ?? 'null'}:${day.date}`}>
        {(provided, snapshot) => {
            return <div className={classNames(
                    'grow shrink-0 relative z-10 isolate',
                    snapshot.isDraggingOver && 'bg-sky-50'
                )}>
                <Title {...{grant, day, appointments: assignedApps, jobs: assignedJobs}} />
                <div ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={classNames(
                        'px-2',
                        'relative min-h-[80vh] h-full w-full grow flex flex-col gap-y-1 rounded-lg',
                        snapshot.isDraggingOver && 'bg-slate-100 ring-brand ring-2 ring-offset-2',
                    )}>
                    <SuggestedJobs {...{grant, day}} />
                    <IncomingJobs {...{grant, day}} />
                    <JobsAndAppointments {...{grant, day, appointments: assignedApps, jobs: assignedJobs}} />
                    {provided.placeholder}

                    {(noJobs && !snapshot.isDraggingOver) && <>
                        <div className="text-center text-sm text-muted my-12">
                            -
                        </div>
                    </>}
                </div>
            </div>
        }}
    </Droppable>
}

const JobsAndAppointments = ({grant, day, appointments, jobs}) => {
    const {hasJobMoved} = useCalendarContext();

    if (!appointments && !jobs) {
        return <Loading className="my-12" center />
    }

    const items = [
        ...appointments.map((appointment, index) => {
            if (['Start', 'End'].indexOf(appointment.type) > -1 || hasJobMoved(appointment.jobId)) {
                return null
            }

            if (appointment.type === 'Break') {
                return {
                    time: appointment.estimatedStartTime ?? 'zzzz',
                    el: <Break key={appointment.appointmentId} {...{appointment}} />
                }
            }

            return {
                time: appointment.estimatedStartTime ?? 'zzzz',
                el: <Appointment key={appointment.appointmentId} {...{appointment, index}} />
            }
        }),
        ...jobs.map((job, index) => {
            if (hasJobMoved(job.id)) {
                return null
            }

            return {
                time: job.estimatedTime ?? 'zzzz',
                el: <Job key={job.id} {...{grant, job, index}} />
            }
        })
    ];

    return items.filter(item => item !== null)
                .sort((a, b) => a.time.localeCompare(b.time))
                .map(item => item.el);
}

const IncomingJobs = ({grant, day}) => {
    const {jobsMovedTo, appointmentWidth} = useCalendarContext();

    const moves = jobsMovedTo(grant?.userId ?? 'null', day.date);

    if (!moves || moves.length === 0) {
        return null;
    }

    return <div className="p-2 border border-brand border-dashed bg-blue-50 rounded-lg flex flex-col gap-1"
            style={{width: appointmentWidth}}
        >
        {moves.map((move, index) => {
            return <Job key={move.jobId} {...{grant, jobId: move.jobId, index}} />
        })}

    </div>
}

const SuggestedJobs = ({grant, day}) => {
    const {planningMode, appointmentWidth, hasJobMoved, suggestedJobs} = useCalendarContext();

    if (planningMode !== 'suggested') {
        return null;
    }

    const userId = (grant?.userId ?? null);
    const relevant = suggestedJobs[day.date]?.[userId] ?? [];
    const filtered = relevant?.filter(job => !hasJobMoved(job.id));

    if (filtered.length === 0) {
        return null;
    }

    return <div className="p-2 pt-1 border border-brand-secondary border-dashed bg-orange-50 rounded-lg flex flex-col gap-1"
            style={{width: appointmentWidth}}
        >
        {filtered.map((job, index) => {
            return <Job key={job.id} {...{grant, job, index}} />
        })}

    </div>
}

const Break = ({appointment}) => {
    const {durationSized, pxPerHour} = useCalendarContext();

    return <div key={appointment.appointmentId}
        className={classNames(
            'rounded-lg w-full overflow-hidden py-0 px-2 min-h-6',
            'bg-slate-200 text-slate-600 border border-slate-300',
            'flex flex-row items-center'
        )}
        style={{
            height: durationSized ? timeToPx(appointment.duration, pxPerHour) : 'auto'
        }}
    >
        <span className="text-sm whitespace-nowrap">
            <strong className="w-[3rem] inline-block"><Time time={appointment.estimatedStartTime} /></strong>
            Break (<Duration time={appointment.duration} />)
        </span>
    </div>
}

const Title = ({grant, day, appointments, jobs}) => {
    const {hasJobMoved, jobsMovedTo} = useCalendarContext();

    const total = calculateTotal(grant, day, appointments, jobs, hasJobMoved, jobsMovedTo);

    return <div className="sticky top-0 w-full p-3 pb-2 bg-white/60 backdrop-blur-md z-10 flex flex-col items-center justify-center mb-2 border-b">
        <Grant grantId={grant?.id ?? null} size="8" />
        <Money pence={total} className="mt-1 text-sm text-muted text-nowrap" />
    </div>
}

const calculateTotal = (grant, day, appointments, jobs, hasJobMoved, jobsMovedTo) => {

    const appointmentsTotal = appointments?.filter(app => !hasJobMoved(app.jobId))
                                            .reduce((acc, app) => acc + parseInt(app?.job?.cost ?? 0), 0) ?? 0;

    const jobsTotal = jobs?.filter(job => !hasJobMoved(job.id))
                            .reduce((acc, job) => acc + jobTotal(job), 0) ?? 0;

    const incomingJobs = jobsMovedTo(grant?.userId ?? 'null', day.date)
                        .reduce((acc, move) => {
                            const job = State.get(['job', move.jobId]);
                            if (!job) {
                                return acc;
                            }
                            return acc + jobTotal(job);
                        }, 0) ?? 0;

    return appointmentsTotal + jobsTotal + incomingJobs;
}

const jobTotal = (job) => {
    const total = parseInt(job?.cost ?? 0)
    const adjustment = parseInt(job?.itemisationAdjustment?.amount ?? 0);

    return total + adjustment;
}

export default DayAppointments;
